import { useTranslation } from "react-i18next";

import {
  Box,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { FormikProps } from "formik";

import { formatEuro } from "../../../../../../../../format/formatNumber";
import { FormikDecimalTextField } from "../../../../../../../../formik/FormikDecimalTextField";
import { AssetExpensePosition, AssetMaintenanceExpenseResponse } from "../../../../../../../api/ClientFileApi";
import { FieldInfo } from "../../../../../personal-data/components/field-info/FieldInfo";
import {
  AssetExpensesFormValues,
  EXPENSE_ROWS,
  ExpenseRowConfig,
  hasPositionInfo,
} from "../../../../ExpensesView.types";

const SECTION = "clients.client.debtSettlement.assetInventory.expensesTab";
const AMOUNT_COLUMN_WIDTH = 200;
const INDENT = 4;
const FIELD_TOP_MARGIN = 2;

interface Props {
  formik: FormikProps<AssetExpensesFormValues>;
  maintenance: AssetMaintenanceExpenseResponse;
  totalExpenses: number;
  livingCostsBelowMinimum: boolean;
}

export function ExpenseTable({ formik, maintenance, totalExpenses, livingCostsBelowMinimum }: Props) {
  const { t, i18n } = useTranslation();
  const isShared = formik.values.paysCostsAlone === false;
  const euroAdornment = { endAdornment: <InputAdornment position="end">€</InputAdornment> };

  const positionLabel = (row: ExpenseRowConfig) => t(`${SECTION}.positions.${row.position}`);

  const amountField = (row: ExpenseRowConfig, column: "amount" | "debtorShare", ariaLabel: string) => {
    const showsLivingCostsWarning =
      livingCostsBelowMinimum && row.position === AssetExpensePosition.LivingCosts && column === "debtorShare";
    return (
      <FormikDecimalTextField
        formik={formik}
        path={`positions.${row.position}.${column}`}
        decimalScale={2}
        minValue={0}
        id={`expense-${row.position}-${column}`}
        formControlProps={{ sx: { mt: FIELD_TOP_MARGIN } }}
        slotProps={{
          input: euroAdornment,
          htmlInput: { "aria-label": ariaLabel },
        }}
        helperText={showsLivingCostsWarning ? t(`${SECTION}.validation.livingCostsMinimum`) : undefined}
      />
    );
  };

  const maintenanceLabel = t(`${SECTION}.table.maintenance`, { count: maintenance.personCount });

  return (
    <Table size="small" aria-label={t(`${SECTION}.table.caption`)}>
      <TableHead>
        <TableRow>
          <TableCell>{t(`${SECTION}.table.position`)}</TableCell>
          <TableCell sx={{ width: AMOUNT_COLUMN_WIDTH }}>{t(`${SECTION}.table.amount`)}</TableCell>
          {isShared && <TableCell sx={{ width: AMOUNT_COLUMN_WIDTH }}>{t(`${SECTION}.table.debtorShare`)}</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          <TableCell component="th">
            <Box display="flex" alignItems="center" gap={0.5}>
              {maintenanceLabel}
              <FieldInfo dense fieldLabel={maintenanceLabel} text={t(`${SECTION}.table.maintenanceInfo`)} />
            </Box>
          </TableCell>
          {isShared && <TableCell>{formatEuro(0, i18n.language)}</TableCell>}
          <TableCell>
            <TextField
              value={formatEuro(maintenance.amount, i18n.language)}
              size="small"
              fullWidth
              helperText=" "
              sx={{
                mt: FIELD_TOP_MARGIN,
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "& .MuiOutlinedInput-input": { px: 0 },
              }}
              slotProps={{
                input: { readOnly: true },
                htmlInput: { "aria-label": maintenanceLabel, tabIndex: -1 },
              }}
            />
          </TableCell>
        </TableRow>
        {EXPENSE_ROWS.map((row) => {
          const label = positionLabel(row);
          return (
            <TableRow key={row.position}>
              <TableCell component="th" sx={{ pl: row.isIndented ? INDENT : undefined }}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  {label}
                  {hasPositionInfo(row.position) && (
                    <FieldInfo dense fieldLabel={label} text={t(`${SECTION}.positionInfo.${row.position}`)} />
                  )}
                </Box>
              </TableCell>
              {isShared ? (
                <>
                  <TableCell>
                    {amountField(row, "amount", t(`${SECTION}.table.amountOf`, { position: label }))}
                  </TableCell>
                  <TableCell>
                    {amountField(row, "debtorShare", t(`${SECTION}.table.debtorShareOf`, { position: label }))}
                  </TableCell>
                </>
              ) : (
                <TableCell>
                  {amountField(row, "debtorShare", t(`${SECTION}.table.amountOf`, { position: label }))}
                </TableCell>
              )}
            </TableRow>
          );
        })}
        <TableRow>
          <TableCell component="th">
            <Typography fontWeight="bold">{t(`${SECTION}.table.total`)}</Typography>
          </TableCell>
          {isShared && <TableCell />}
          <TableCell>
            <Typography fontWeight="bold">{formatEuro(totalExpenses, i18n.language)}</Typography>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}
